import { effect, inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChartDataset, ChartOptions } from 'chart.js';
import {
  Bucket,
  BucketGroup,
  ChartSeverities,
  DbxChartData,
  PartnerPentestStatistics,
  PentestScan,
  StatisticTypes,
} from '../models';
import { ThemeService } from './theme.service';
import { CustomerWeekStatistics } from '../models/statistics.data';
import { endOfDay, startOfDay, subDays, subWeeks } from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class ChartService {
  private readonly translate = inject(TranslateService);
  private readonly themeService = inject(ThemeService);
  private bodyTitleColor: string;
  private bodyTextColor: string;

  private readonly severities: { key: string; colorVar: string; labelKey: string }[] = [
    { key: 'critical', colorVar: '--app-critical-container', labelKey: 'common.criticality.critical' },
    { key: 'high', colorVar: '--app-high-container', labelKey: 'common.criticality.high' },
    { key: 'medium', colorVar: '--app-medium-container', labelKey: 'common.criticality.medium' },
    { key: 'low', colorVar: '--app-low-container', labelKey: 'common.criticality.low' },
  ];
  private style: CSSStyleDeclaration;
  private cardBackground: string;
  private lowColor: string;
  private infoColor: string;
  private highColor: string;
  private criticalColor: string;
  private outlineVariant: string;

  constructor() {
    this.style = getComputedStyle(document.body);
    this.bodyTitleColor = this.style.getPropertyValue('--body-title-color').trim();
    this.bodyTextColor = this.style.getPropertyValue('--body-text-color').trim();
    this.cardBackground = this.style.getPropertyValue('--card-background').trim();
    this.criticalColor = this.style.getPropertyValue('--app-critical-container').trim();
    this.highColor = this.style.getPropertyValue('--app-high-container').trim();
    this.lowColor = this.style.getPropertyValue('--app-low-container').trim();
    this.infoColor = this.style.getPropertyValue('--app-info-container').trim();
    this.outlineVariant = this.style.getPropertyValue('--mat-sys-outline-variant').trim();

    effect(() => {
      this.themeService.theme(); // get new colors on theme change
      this.bodyTitleColor = this.style.getPropertyValue('--body-title-color').trim();
      this.bodyTextColor = this.style.getPropertyValue('--body-text-color').trim();
      this.cardBackground = this.style.getPropertyValue('--card-background').trim();
      this.criticalColor = this.style.getPropertyValue('--app-critical-container').trim();
      this.highColor = this.style.getPropertyValue('--app-high-container').trim();
      this.lowColor = this.style.getPropertyValue('--app-low-container').trim();
      this.infoColor = this.style.getPropertyValue('--app-info-container').trim();
      this.outlineVariant = this.style.getPropertyValue('--mat-sys-outline-variant').trim();
    });
  }

  createStackedChartData(scanStatistics: BucketGroup[]): DbxChartData<'line'> {
    const datasets: ChartDataset<'line', any>[] = this.severities
      .flatMap((severity) => {
        const color = this.style.getPropertyValue(severity.colorVar).trim();
        const data = scanStatistics.map((u: any) => {
          return {
            x: u.date,
            y: (u[severity.key] || 0) > 0 ? u[severity.key] : 0.1, // needs to be at least 0.1 to be shown properly and to allow the curve to start at 0
            scans: u.scans?.[severity.key] || [],
            customerIds: u.customerIds?.[severity.key] || [],
            companies: u.companies?.[severity.key] || [],
            scanIds: u.scanIds?.[severity.key] || [],
          };
        });

        const hasData = scanStatistics.some((u: any) => (u[severity.key] || 0) > 0);

        return {
          label: this.translate.instant(severity.labelKey),
          severityKey: severity.labelKey,
          data,
          borderColor: color,
          backgroundColor: this.blendColors(color, this.cardBackground, 0.25),
          tension: 0.4,
          borderWidth: 2,
          pointBackgroundColor: color,
          pointBorderColor: color,
          pointRadius: (context: any) => {
            const value = context.raw?.y;
            return value === 0.1 ? 0 : 3;
          },
          pointHitRadius: (context: any) => {
            const value = context.raw?.y;
            return value === 0.1 ? 0 : 10;
          },
          fill: true,
          spanGaps: true,
          hidden: !hasData,
        } as ChartDataset<'line', any>;
      })
      .filter((ds) => !ds.hidden);

    return {
      labels: scanStatistics.map((u) => u.date),
      datasets: datasets,
    } as DbxChartData<'line', any>;
  }

  private createWeeklyBuckets(): Bucket[] {
    const buckets: Bucket[] = [];
    const now = endOfDay(new Date());

    for (let i = 0; i < 4; i++) {
      const end = endOfDay(subWeeks(now, i));
      const start = startOfDay(subDays(end, 7));

      const label = `${this.toDateString(start)} - ${this.toDateString(end)}`;

      buckets.unshift({
        start: start.getTime(),
        end: end.getTime(),
        label,
      });
    }
    return buckets;
  }

  private toDateString(start: Date) {
    return start.toLocaleDateString(this.translate.currentLang, { day: '2-digit', month: '2-digit' });
  }

  private createEmptyBucketGroups(buckets: Bucket[]): Record<string, BucketGroup> {
    const groupedByBucket: Record<string, BucketGroup> = {};
    buckets.forEach((bucket) => {
      groupedByBucket[bucket.label] = {
        date: bucket.label,
        low: 0,
        medium: 0,
        high: 0,
        critical: 0,
        scans: { low: [], medium: [], high: [], critical: [] },
        customerIds: { low: [], medium: [], high: [], critical: [] },
        companies: { low: [], medium: [], high: [], critical: [] },
        scanIds: { low: [], medium: [], high: [], critical: [] },
        timestamp: bucket.start,
      };
    });
    return groupedByBucket;
  }

  private assignStatisticsToBuckets(
    stats: any[],
    buckets: Bucket[],
    groupedByBucket: Record<string, BucketGroup>,
    type: StatisticTypes,
    customerId?: number,
    customerName?: string,
  ): void {
    if (Array.isArray(stats)) {
      stats.forEach((s) => {
        const completedAt = new Date(s.pentestScan.completedAt).getTime();
        const chunk = buckets.find((c) => completedAt >= c.start && completedAt <= c.end);

        if (chunk && groupedByBucket[chunk.label]) {
          const target = groupedByBucket[chunk.label];
          const statsForType = s.statistics[type];
          target.low += statsForType.low || 0;
          target.medium += statsForType.medium || 0;
          target.high += statsForType.high || 0;
          target.critical += statsForType.critical || 0;

          const severities: ChartSeverities[] = ['low', 'medium', 'high', 'critical'];
          severities.forEach((key) => {
            const count = statsForType[key] || 0;
            if (count > 0) {
              if (customerId) {
                target.customerIds[key].push(customerId);
              }
              if (customerName) {
                const companyInfo = `${customerName}: ${count}`;
                target.companies[key].push(companyInfo);
              } else {
                const scanInfo = `${s.pentestScan.name}: ${count}`;
                target.scans[key].push(scanInfo);
              }
              target.scanIds[key].push(s.pentestScan.id);
            }
          });
        }
      });
    }
  }

  private aggregateCompaniesInBucket(data: BucketGroup): void {
    (Object.keys(data.companies) as ChartSeverities[]).forEach((key) => {
      const customerCounts: Record<string, number> = {};
      data.companies[key].forEach((entry: string) => {
        const [name, countStr] = entry.split(': ');
        const count = parseInt(countStr, 10);
        customerCounts[name] = (customerCounts[name] || 0) + count;
      });
      data.companies[key] = Object.entries(customerCounts).map(([name, count]) => `${name}: ${count}`);
    });
  }

  private calculateMaxFindingsValue(chartData: BucketGroup[]): number {
    let maxStackedValue = 0;
    chartData.forEach((chunk) => {
      const sum = (chunk.critical || 0) + (chunk.high || 0) + (chunk.medium || 0) + (chunk.low || 0);
      if (sum > maxStackedValue) {
        maxStackedValue = sum;
      }
    });
    return maxStackedValue;
  }

  aggregateWeeklyStackedChartData(stats: any[], type: StatisticTypes): DbxChartData<'line', any> {
    const buckets = this.createWeeklyBuckets();
    const groupedByBucket = this.createEmptyBucketGroups(buckets);
    this.assignStatisticsToBuckets(stats, buckets, groupedByBucket, type);

    const chartData = buckets.map((chunk) => groupedByBucket[chunk.label]);
    const result = this.createStackedChartData(chartData);
    result.maxStackedValue = this.calculateMaxFindingsValue(chartData);
    return result;
  }

  aggregatePartnerWeeklyStackedChartData(
    stats: PartnerPentestStatistics[],
    type: StatisticTypes,
  ): DbxChartData<'line', any> {
    const buckets = this.createWeeklyBuckets();
    const groupedByBucket = this.createEmptyBucketGroups(buckets);

    stats.forEach((partnerStat) => {
      this.assignStatisticsToBuckets(
        partnerStat.statistics,
        buckets,
        groupedByBucket,
        type,
        partnerStat.customer.id,
        partnerStat.customer.companyName,
      );
    });

    const chartData = buckets.map((chunk) => {
      const data = groupedByBucket[chunk.label];
      this.aggregateCompaniesInBucket(data);
      return data;
    });

    const result = this.createStackedChartData(chartData);
    result.maxStackedValue = this.calculateMaxFindingsValue(chartData);
    return result;
  }

  private getGridColor = (context: any) => {
    const value = context.tick?.value;
    if (value === 0.1 || (value >= 1 && Number.isInteger(value))) {
      return this.outlineVariant;
    }
    return 'transparent';
  };

  createStackedLineChartOptions(
    titleKey: string,
    suggestedMax?: number,
    onClick?: (data: any, severityKey: string) => void,
  ): ChartOptions<'line'> {
    return {
      responsive: true,
      onClick: (event: any, elements: any, chart: any) => {
        if (onClick && elements.length > 0) {
          const element = elements[0];
          const datasetIndex = element.datasetIndex;
          const index = element.index;
          const dataset = chart.data.datasets[datasetIndex];
          const severityKey = dataset.severityKey;
          const dataPoint = dataset.data[index];
          const { x, y, ...data } = dataPoint;
          onClick(data, severityKey);
        }
      },
      plugins: {
        title: {
          display: false,
          text: this.translate.instant(titleKey),
          color: this.bodyTitleColor,
          font: {
            size: 13,
            weight: 400,
          },
          padding: {
            top: 0,
            bottom: 15,
          },
          align: 'start',
        },
        legend: {
          display: false,
          position: 'bottom',
          labels: {
            font: { size: 10 },
            color: this.bodyTextColor,
            boxWidth: 10,
          },
        },
        tooltip: {
          boxPadding: 4,
          bodyAlign: 'left',
          callbacks: {
            label: (context: any) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              const displayValue = value === 0.1 ? 0 : value;
              return `${label}: ${displayValue}`;
            },
            afterBody: (context: any) => {
              const raw = context[0].raw;
              const companies = raw?.companies || [];
              if (companies.length > 0) {
                return [' ', ...companies];
              }
              const scans = raw?.scans || [];
              return scans.length > 0 ? [' ', ...scans] : [];
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            font: { size: 10 },
            color: this.bodyTextColor,
            maxRotation: 0,
            minRotation: 0,
            autoSkip: false,
          },
        },
        y: {
          type: 'logarithmic',
          min: 0.1, // needs to be at least 0.1 to be shown properly and to allow the curve to start at 0
          suggestedMax: suggestedMax || 100,
          grid: {
            color: this.getGridColor,
          },
          ticks: {
            font: { size: 10 },
            color: this.bodyTextColor,
            callback: (value: any) => {
              if (value === 0.1) return '0';
              return value >= 1 && Number.isInteger(value) ? value.toString() : '';
            },
          },
          stacked: true,
        },
      },
    };
  }

  getNiceMax(max: number): number {
    if (max < 10) {
      return 10;
    }

    const exponent = Math.floor(Math.log10(max));
    const fraction = max / Math.pow(10, exponent);

    let niceFraction;
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;

    return niceFraction * Math.pow(10, exponent);
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } {
    hex = hex.replace(/^#/, '');
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((char) => char + char)
        .join('');
    }
    const bigint = parseInt(hex, 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  }

  private blendColors(foregroundHex: string, backgroundHex: string, opacity: number): string {
    try {
      const fg = this.hexToRgb(foregroundHex);
      const bg = this.hexToRgb(backgroundHex);

      const r = Math.round(fg.r * opacity + bg.r * (1 - opacity));
      const g = Math.round(fg.g * opacity + bg.g * (1 - opacity));
      const b = Math.round(fg.b * opacity + bg.b * (1 - opacity));

      return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
    } catch (e) {
      return foregroundHex;
    }
  }

  createPartnerBarChartOptions(
    titleKey: string,
    onClick?: (customerId: number, customerName: string) => void,
  ): ChartOptions<'bar'> {
    return {
      responsive: true,
      onClick: (event: any, elements: any, chart: any) => {
        if (onClick && elements.length > 0) {
          const element = elements[0];
          const datasetIndex = element.datasetIndex;
          const index = element.index;
          const dataset = chart.data.datasets[datasetIndex];
          const dataPoint = dataset.data[index];
          const { x, customerId } = dataPoint;
          onClick(customerId, x);
        }
      },
      maintainAspectRatio: true,
      aspectRatio: 2.5,
      indexAxis: 'x',
      plugins: {
        title: {
          display: false,
          text: this.translate.instant(titleKey),
          color: this.bodyTitleColor,
          font: {
            size: 13,
            weight: 400,
          },
          padding: {
            top: 0,
            bottom: 15,
          },
          align: 'start',
        },
        legend: {
          display: false,
        },
        tooltip: {
          boxPadding: 4,
          bodyAlign: 'left',
          callbacks: {
            label: (context: any) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              const displayValue = value === 0.1 ? 0 : value;
              return `${label}: ${displayValue}`;
            },
            afterBody: (context: any) => {
              const raw = context[0].raw;
              const scans = ((raw?.scans || []) as PentestScan[]).map((it) => it.name);
              return scans.length > 0 ? [' ', ...scans] : [];
            },
          },
        },
      },
      scales: {
        y: {
          type: 'logarithmic',
          min: 0.1,
          suggestedMax: 10,
          grid: {
            color: this.getGridColor,
          },
          ticks: {
            font: { size: 10 },
            color: this.bodyTextColor,
            maxRotation: 0,
            minRotation: 0,
            callback: (value: any) => {
              if (value === 0.1) return '0';
              return value >= 1 && Number.isInteger(value) ? value.toString() : '';
            },
          },
          stacked: true,
        },
        x: {
          stacked: true,
          ticks: {
            padding: 10,
            font: { size: 10 },
            color: this.bodyTextColor,
          },
          grid: {
            drawTicks: false,
            drawOnChartArea: false,
          },
        },
      },
    };
  }

  private createBarDataset(labelTranslationKey: string, data: any[], color: string): ChartDataset<'bar', any> {
    const label = this.translate.instant(labelTranslationKey);
    return {
      label,
      data,
      backgroundColor: color,
      borderColor: color,
      borderWidth: 1,
      barThickness: 30,
      minBarLength: 5,
    };
  }

  aggregatePartnerTopCriticalHighIncreases(stats: CustomerWeekStatistics[]): DbxChartData<'bar', any> {
    const datasets: ChartDataset<'bar', any>[] = [
      this.createBarDataset(
        'common.criticality.critical',
        stats.map((c) => ({
          x: c.customer.companyName,
          y: c.statistics.criticalChange,
          customerId: c.customer.id,
          scans: c.statistics.criticalScans,
        })),
        this.criticalColor,
      ),
      this.createBarDataset(
        'common.criticality.high',
        stats.map((c) => ({
          x: c.customer.companyName,
          y: c.statistics.highChange,
          customerId: c.customer.id,
          scans: c.statistics.highScans,
        })),
        this.highColor,
      ),
    ];

    return {
      labels: stats.map((c) => c.customer.companyName),
      datasets: datasets,
    } as DbxChartData<'bar', any>;
  }

  aggregatePartnerTopCriticalHighDecreases(stats: CustomerWeekStatistics[]): DbxChartData<'bar', any> {
    const datasets: ChartDataset<'bar', any>[] = [
      this.createBarDataset(
        'common.criticality.critical',
        stats.map((c) => ({
          x: c.customer.companyName,
          y: c.statistics.criticalChange * -1,
          customerId: c.customer.id,
          scans: c.statistics.scans,
        })),
        this.infoColor,
      ),
      this.createBarDataset(
        'common.criticality.high',
        stats.map((c) => ({
          x: c.customer.companyName,
          y: c.statistics.highChange * -1,
          customerId: c.customer.id,
          scans: c.statistics.scans,
        })),
        this.lowColor,
      ),
    ];

    return {
      labels: stats.map((c) => c.customer.companyName),
      datasets: datasets,
    } as DbxChartData<'bar', any>;
  }
}
